import React, { FC, TouchEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPopupContentAction } from '~/src/redux/popups/popups.slice';
import { useMatchMedia } from '~/src/hooks/use-match-media.hook';
import { selectCurrentRegion, selectIsShowGeolocationBlock } from '~/src/redux/geolocation/geolocation.selectors';
import { GeolocationModalContent } from '~/src/components/common-components/geolocation-modal-content/geolocation-modal-content';
import { BurgerMenuButton } from '~/src/components/mini-sections/referral-product-header/components/burger-menu-button/burger-menu-button';
import { SvgMtsLogoIcon } from '~/src/components/svg-components/mts-logo-icon';
import { SvgGeoIcon } from '~/src/components/svg-components/geo-icon';
import { MediaWidthEnum } from '~/src/models/enums/media-width.enum';
import type { RegionsDataInterface } from '~/src/repositories/regions/regions.repository';
import type { NavItem } from './referral-product-header.types';
import * as Styled from './referral-product-header.styles';

interface GeolocationBlockProps {
    regionsData: RegionsDataInterface[] | [];
    closePopup: () => void;
    headerTitle?: string;
    disableGeolocationModal?: boolean;
    navItems?: NavItem[];
    logoAsLinkToHome?: boolean;
}

export const ReferralProductHeader: FC<GeolocationBlockProps> = ({
    regionsData,
    closePopup,
    headerTitle = 'Бизнес',
    disableGeolocationModal,
    navItems,
    logoAsLinkToHome = false,
}) => {
    const dispatch = useDispatch();
    const currentRegion = useSelector(selectCurrentRegion);
    const isShowGeolocationBlock = useSelector(selectIsShowGeolocationBlock);
    const [isMobile] = useMatchMedia(MediaWidthEnum.mobile);
    const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
    const [tappedNavItemId, setTappedNavItemId] = useState({ x: 0, width: 0, id: -1 });

    useEffect(() => {
        document.body.style.overflow = mobileMenuIsOpen && isMobile ? 'hidden' : 'auto';
    }, [mobileMenuIsOpen, isMobile]);

    const handleChangeGeolocation = () => {
        if (!disableGeolocationModal) {
            dispatch(
                setPopupContentAction({
                    content: <GeolocationModalContent onCloseClick={closePopup} regionsData={regionsData} />,
                    replace: true,
                }),
            );
        }
    };

    const toggleMobileMenu = () => {
        setMobileMenuIsOpen((prev) => !prev);
    };

    const onTouchStart = (e: TouchEvent, itemId: number) => {
        const test = e.currentTarget.getBoundingClientRect();
        const touchEvent = e.touches[0];

        setTappedNavItemId({ x: touchEvent.clientX, width: test.width, id: itemId });
    };

    const onTouchEnd = () => setTappedNavItemId({ x: 0, width: 0, id: -1 });

    const renderHeaderLogo = () => {
        const logoWithText = (
            <>
                <SvgMtsLogoIcon />
                <Styled.ReferralProductHeaderText>{headerTitle}</Styled.ReferralProductHeaderText>
            </>
        );

        if (logoAsLinkToHome) {
            return <Styled.HeaderLogoLink href="/">{logoWithText}</Styled.HeaderLogoLink>;
        }

        return <Styled.HeaderLogoContainer>{logoWithText}</Styled.HeaderLogoContainer>;
    };

    return (
        <Styled.ReferralProductHeaderContainer isShowGeolocationBlock={isShowGeolocationBlock}>
            <Styled.ReferralProductHeader>
                <Styled.ReferralProductHeaderLogoWrapper>{renderHeaderLogo()}</Styled.ReferralProductHeaderLogoWrapper>
                {navItems && (
                    <Styled.NavigationBlock mobileMenuIsOpen={mobileMenuIsOpen}>
                        <Styled.NavItemList>
                            {navItems.map((item) => {
                                return (
                                    <Styled.NavItem
                                        key={item.id}
                                        onTouchStart={(e) => onTouchStart(e, item.id)}
                                        onTouchEnd={onTouchEnd}
                                        itemIsTouched={item.id === tappedNavItemId.id}
                                        x={tappedNavItemId.x}
                                        width={tappedNavItemId.width}
                                    >
                                        <Styled.NavItemLink href={item.href} target={item.target}>
                                            {item.label}
                                        </Styled.NavItemLink>
                                    </Styled.NavItem>
                                );
                            })}
                        </Styled.NavItemList>
                    </Styled.NavigationBlock>
                )}
                <Styled.MobileMenuWithGeolocation>
                    <Styled.ReferralProductHeaderGeoWrapper onClick={handleChangeGeolocation}>
                        <Styled.ReferralProductHeaderGeoIconWrapper>
                            <SvgGeoIcon />
                        </Styled.ReferralProductHeaderGeoIconWrapper>
                        <Styled.ReferralProductHeaderGeoText withBurger={!!navItems}>
                            {currentRegion?.name}
                        </Styled.ReferralProductHeaderGeoText>
                    </Styled.ReferralProductHeaderGeoWrapper>
                    {navItems && (
                        <Styled.MobileMenuContainer>
                            <BurgerMenuButton isOpen={mobileMenuIsOpen} toggleMenu={toggleMobileMenu} />
                        </Styled.MobileMenuContainer>
                    )}
                </Styled.MobileMenuWithGeolocation>
            </Styled.ReferralProductHeader>
        </Styled.ReferralProductHeaderContainer>
    );
};
